import React, { useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import cx from 'classnames';

import RoundedButton from 'components/RoundedButton/RoundedButton';
import SpinnerRing from 'components/SpinnerRing';
import Toast from 'components/Toast/Toast';
import useToast from 'hooks/useToast';

import CreateSubscriptionPackageModal from './components/CreateSubscriptionPackageModal';
import {
  useMutationCreateSubscriptionPackage,
  useMutationRetrySubscriptionPackageSync,
  useQuerySubscriptionPackages,
  useQuerySubscriptionPackagesMeta,
} from './hooks/useSubscriptionPackages';
import styles from './SubscriptionPackages.module.scss';

const DEFAULT_FILTERS = {
  page: 1,
  limit: 10,
  search: '',
  country: '',
  status: '',
};

const SYNC_STATUSES = ['PENDING', 'SYNCED', 'FAILED'];
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const COUNTRY_LABELS = {
  '*': 'All countries',
  ARE: 'United Arab Emirates',
  KSA: 'Saudi Arabia',
  UAE: 'United Arab Emirates',
};

const getPageNumbers = (currentPage, totalPages) => {
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, startPage + 4);
  const firstPage = Math.max(1, endPage - 4);

  return Array.from({ length: endPage - firstPage + 1 }, (_, index) => firstPage + index);
};

const formatEnum = (value) =>
  String(value || '')
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatCountry = (country) => COUNTRY_LABELS[country] || formatEnum(country);
const formatCountryFilter = (country) => (country === '*' ? 'All-country packages' : formatCountry(country));

const formatDate = (value) => {
  if (!value) return 'Never';
  return new Date(value).toLocaleString();
};

const getErrorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const SubscriptionPackages = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const queryParams = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== '' && value !== null && value !== undefined)
      ),
    [filters]
  );

  const metaQuery = useQuerySubscriptionPackagesMeta();
  const packagesQuery = useQuerySubscriptionPackages(queryParams);
  const createMutation = useMutationCreateSubscriptionPackage();
  const retryMutation = useMutationRetrySubscriptionPackageSync();

  const meta = metaQuery.data || {
    pricingModels: [],
    pricingUnits: [],
    billingPeriods: [],
    chargebeeConnections: [],
    countries: [],
  };
  const configuredCountries = (meta.chargebeeConnections || []).map((connection) => connection.country);
  const filterCountries = meta.countries || [];
  const response = packagesQuery.data || {};
  const packages = response.data || [];
  const total = response.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / filters.limit));
  const pageNumbers = getPageNumbers(filters.page, totalPages);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value, page: 1 }));
  };

  const refreshList = () => queryClient.invalidateQueries({ queryKey: ['subscription-packages', 'list'] });

  const handleCreatePackage = async (payload) => {
    try {
      const result = await createMutation.mutateAsync(payload);
      await refreshList();

      if (result.package?.chargebeeSyncStatus === 'FAILED') {
        toast.showWarning('Package saved but Chargebee sync failed');
      } else {
        toast.showSuccess('Subscription package created and synced');
      }
    } catch (error) {
      toast.showError(getErrorMessage(error, 'Failed to create subscription package'));
      throw error;
    }
  };

  const handleRetrySync = (subscriptionPackage) => {
    retryMutation.mutate(subscriptionPackage.id, {
      onSuccess: async (result) => {
        await refreshList();
        if (result.package?.chargebeeSyncStatus === 'FAILED') {
          toast.showWarning('Chargebee sync failed again');
        } else {
          toast.showSuccess('Chargebee sync completed');
        }
      },
      onError: (error) => {
        toast.showError(getErrorMessage(error, 'Failed to retry Chargebee sync'));
      },
    });
  };

  const isLoading = metaQuery.isFetching || packagesQuery.isFetching;
  const isMutating = createMutation.isPending || retryMutation.isPending;
  const renderPagination = () => (
    <div className={styles.pagination}>
      <div className={styles.pageSummary}>
        <span>
          Page {filters.page} of {totalPages} / {total} rows
        </span>
        <label>
          <span>Rows per page</span>
          <select
            aria-label='Rows per page'
            value={filters.limit}
            onChange={(event) => {
              setFilters((current) => ({
                ...current,
                limit: Number(event.target.value),
                page: 1,
              }));
            }}
          >
            {PAGE_SIZE_OPTIONS.map((limit) => (
              <option key={limit} value={limit}>
                {limit}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className={styles.pageControls}>
        <RoundedButton
          text='Previous'
          type='button'
          btnStyle='outlined'
          size='sm'
          disabled={filters.page <= 1}
          onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}
        />
        {pageNumbers.map((pageNumber) => (
          <button
            key={pageNumber}
            type='button'
            className={cx(styles.pageButton, {
              [styles.activePageButton]: filters.page === pageNumber,
            })}
            aria-current={filters.page === pageNumber ? 'page' : undefined}
            onClick={() => setFilters((current) => ({ ...current, page: pageNumber }))}
          >
            {pageNumber}
          </button>
        ))}
        <RoundedButton
          text='Next'
          type='button'
          btnStyle='outlined'
          size='sm'
          disabled={filters.page >= totalPages}
          onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
        />
      </div>
    </div>
  );

  return (
    <>
      <div className={cx(styles.pageShell, 'mt-3 mx-3')}>
        <div className={cx(styles.wrapper, 'panel p-0')}>
          <div className='px-5 py-4 border-btm d-flex align-items-center justify-content-between'>
            <div>
              <h2>Subscription Packages</h2>
              <p className='tab-subtitle'>Create packages and track Chargebee sync state</p>
            </div>
            <RoundedButton
              icon='icon-plus'
              text='Add New'
              color='primary'
              size='sm'
              onClick={() => setIsCreateModalOpen(true)}
              disabled={metaQuery.isFetching || configuredCountries.length === 0}
            />
          </div>

          <div className={styles.filters}>
            <label>
              <span>Search</span>
              <input
                aria-label='Search subscription packages'
                value={filters.search}
                onChange={(event) => updateFilter('search', event.target.value)}
                placeholder='Identifier, name or platform name'
              />
            </label>
            <label>
              <span>Country</span>
              <select value={filters.country} onChange={(event) => updateFilter('country', event.target.value)}>
                <option value=''>All countries</option>
                {filterCountries.map((country) => (
                  <option key={country} value={country}>
                    {formatCountryFilter(country)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Status</span>
              <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}>
                <option value=''>All statuses</option>
                {SYNC_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {formatEnum(status)}
                  </option>
                ))}
              </select>
            </label>
            <RoundedButton
              text='Reset'
              type='button'
              btnStyle='outlined'
              size='sm'
              onClick={() => setFilters(DEFAULT_FILTERS)}
            />
          </div>

          {isLoading && packages.length === 0 ? (
            <SpinnerRing />
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Identifier</th>
                    <th>Platform Name</th>
                    <th>Country</th>
                    <th>Currency</th>
                    <th>Price</th>
                    <th>Model</th>
                    <th>Billing</th>
                    <th>Status</th>
                    <th>Deprecated</th>
                    <th>Last Sync</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.length === 0 ? (
                    <tr>
                      <td colSpan='11' className={styles.emptyCell}>
                        No subscription packages found
                      </td>
                    </tr>
                  ) : (
                    packages.map((subscriptionPackage) => (
                      <tr key={subscriptionPackage.id}>
                        <td>
                          <strong>{subscriptionPackage.identifier}</strong>
                          <span>{subscriptionPackage.name}</span>
                        </td>
                        <td>{subscriptionPackage.platformName}</td>
                        <td>{formatCountry(subscriptionPackage.country)}</td>
                        <td>{subscriptionPackage.currency}</td>
                        <td>{subscriptionPackage.defaultUnitPrice}</td>
                        <td>{formatEnum(subscriptionPackage.pricingModel)}</td>
                        <td>{formatEnum(subscriptionPackage.defaultBillingPeriod)}</td>
                        <td>
                          <span
                            className={cx(
                              styles.statusBadge,
                              styles[String(subscriptionPackage.chargebeeSyncStatus || '').toLowerCase()]
                            )}
                          >
                            {formatEnum(subscriptionPackage.chargebeeSyncStatus)}
                          </span>
                          {subscriptionPackage.lastChargebeeSyncError && (
                            <small title={subscriptionPackage.lastChargebeeSyncError}>
                              {subscriptionPackage.lastChargebeeSyncError}
                            </small>
                          )}
                        </td>
                        <td>{subscriptionPackage.deprecated ? 'Yes' : 'No'}</td>
                        <td>{formatDate(subscriptionPackage.lastChargebeeSyncAt)}</td>
                        <td>
                          {subscriptionPackage.chargebeeSyncStatus === 'FAILED' ? (
                            <button
                              type='button'
                              className={styles.retryButton}
                              onClick={() => handleRetrySync(subscriptionPackage)}
                              disabled={retryMutation.isPending}
                            >
                              Retry sync
                            </button>
                          ) : (
                            <span className={styles.noAction}>-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {renderPagination()}
        </div>
      </div>

      <CreateSubscriptionPackageModal
        isOpen={isCreateModalOpen}
        meta={meta}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreatePackage}
        isSubmitting={createMutation.isPending}
      />
      {isMutating && <div className={styles.savingOverlay}>Syncing package</div>}
      <Toast {...toast.toast} onClose={toast.hideToast} />
    </>
  );
};

export default SubscriptionPackages;
